import { Chart, KPI } from '@/contexts/AdminContext/dashboardProvider';
import Skeleton from '@common/components/Skeleton';
import { Icon, Tooltip } from '@common/index';
import {
    Trash2, TrendingDown, TrendingUp
} from 'lucide-react';
import React from 'react';
import { GridLayout, Layout } from 'react-grid-layout';
import { ChartRenderer } from './ChartRenderer';
import DataSourceIcons from './DataSourceIcons';
import styles from './index.module.scss';
import MetricTooltip from './MetricTooltip';
import { GridItem } from './types';
import {
    extractNumber, formatCompactNumber, getFormattedKPIValue
} from './utils/dashboardUtils';

interface DashboardGridProps {
    className?: string;
    layout: Layout;
    items: GridItem[];
    responsiveCols: number;
    containerWidth: number;
    isEditMode: boolean;
    isLoading: boolean;
    draggingId: string | null;
    chartsReady: boolean;
    
    onLayoutChange?: (layout: Layout) => void;
    onResizeStart?: (layout: any, oldItem: any, newItem: any) => void;
    onResize?: (layout: any, oldItem: any, newItem: any) => void;
    onResizeStop?: (layout: any, oldItem: any, newItem: any) => void;
    onDragStart?: (layout: any, oldItem: any, newItem: any) => void;
    onDragStop?: (layout: any, oldItem: any, newItem: any) => void;
    
    handleDelete: (key: string) => void;
    openBreakdown: (type: 'kpi' | 'chart', data: KPI | Chart) => void;
    handleAskAi: (type: 'kpi' | 'chart', data: KPI | Chart) => void;
}

const renderKPIDelta = (kpi: KPI) => {
    const currArr = Array.isArray(kpi.data) ? kpi.data : [];
    const prevArr = Array.isArray(kpi.prevData) ? kpi.prevData : [];
    if (currArr.length === 0) return null;

    const currRaw = (currArr[0] as any)[kpi.column];
    const prevRaw = prevArr.length > 0 ? (prevArr[0] as any)[kpi.column] : undefined;

    const currValExtracted = currRaw !== undefined && currRaw !== null ? extractNumber(currRaw) : 0;
    const prevValExtracted = prevRaw !== undefined && prevRaw !== null ? extractNumber(prevRaw) : 0;

    const currVal = typeof currValExtracted === 'number' ? currValExtracted : 0;
    const prevVal = typeof prevValExtracted === 'number' ? prevValExtracted : 0;

    // 前值是 0 时不展示 delta
    if (prevVal === 0) return null;

    const diff = currVal - prevVal;
    const pct = prevVal !== 0 ? (diff / prevVal) * 100 : null;
    const isPositive = diff > 0;
    const isNegative = diff < 0;
    const badgeCls = isPositive ? 'up' : (isNegative ? 'down' : 'neutral');

    let ArrowIcon = null;
    if (isPositive) ArrowIcon = <TrendingUp size={12} />;
    else if (isNegative) ArrowIcon = <TrendingDown size={12} />;

    const pctStr = pct === null ? '—' : `${Math.abs(pct).toFixed(2)}%`;
    const prevValStr = formatCompactNumber(prevVal);

    return (
        <div className={`${styles.trendBadge} ${styles[badgeCls]}`}>
            {ArrowIcon}
            <span>{`${pctStr}`}</span>
        </div>
    );
};

export const DashboardGrid: React.FC<DashboardGridProps> = ({
    className,
    layout,
    items,
    responsiveCols,
    containerWidth,
    isEditMode,
    isLoading,
    draggingId,
    chartsReady,
    onLayoutChange,
    onResizeStart,
    onResize,
    onResizeStop,
    onDragStart,
    onDragStop,
    handleDelete,
    openBreakdown,
    handleAskAi
}) => {
    return (
        <GridLayout
            className={className}
            layout={layout}
            gridConfig={{
                cols: responsiveCols,
                rowHeight: 68,
                margin: [16, 16]
            }}
            width={containerWidth}
            dragConfig={{ enabled: isEditMode }}
            resizeConfig={{ enabled: isEditMode }}
            onLayoutChange={onLayoutChange}
            onResizeStart={onResizeStart}
            onResize={onResize}
            onResizeStop={onResizeStop}
            onDragStart={onDragStart}
            onDragStop={onDragStop}
        >
            {items.map((item) => (
                <div
                    key={item.key}
                    id={`dashboard-item-${item.key}`}
                    className={styles.gridItemWrapper}
                >
                    <div className={`${styles.gridItem} ${item.type === 'kpi' ? styles.kpiItem : styles.chartItem} ${draggingId === item.key ? styles.dragging : ''}`}>
                        {/* KPI Rendering */}
                        {item.type === 'kpi' && (
                            isLoading ? (
                                <Skeleton
                                    width="100%"
                                    height="100%"
                                    style={{ borderRadius: 24 }}
                                />
                            ) : (
                                <div className={styles.kpiCard}>
                                    <div className={styles.kpiHeader}>
                                        <DataSourceIcons sources={(item.data as KPI).sources} />
                                        <div className={styles.cardControls}>
                                            {isEditMode ? (
                                                <>
                                                    <Tooltip content="Remove">
                                                        <button
                                                            className={styles.deleteButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(item.key);
                                                            }}
                                                        >
                                                            <Trash2 size={16} />
                                                            <span className={styles.deleteText}>Remove</span>
                                                        </button>
                                                    </Tooltip>
                                                    <Tooltip content="Drill down">
                                                        <button
                                                            className={styles.drillDownButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openBreakdown('kpi', item.data);
                                                            }}
                                                        >
                                                            <Icon name="arrow_fat" size={16} />
                                                        </button>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <Tooltip content="Ask AI">
                                                        <div
                                                            className={styles.askAi}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleAskAi(item.type, item.data);
                                                            }}
                                                        >
                                                            <Icon name="sparkles" size={16} />
                                                            <span className={styles.askAiText}>Ask AI</span>
                                                        </div>
                                                    </Tooltip>
                                                    <Tooltip content="Drill down">
                                                        <button
                                                            className={styles.drillDownButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openBreakdown('kpi', item.data);
                                                            }}
                                                        >
                                                            <Icon name="arrow_fat" size={16} />
                                                        </button>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.kpiContent}>
                                        <div className={styles.kpiTitleRow}>
                                            <MetricTooltip
                                                title={item.data.title}
                                                formula={(item.data as KPI).formula}
                                                detail={(item.data as KPI).sql_detail}
                                                sql={(item.data as KPI).sql}
                                            >
                                                <span className={styles.kpiTitle}>{item.data.title}</span>
                                            </MetricTooltip>
                                        </div>

                                        <div className={styles.kpiValueRow}>
                                            <span className={styles.kpiValue} title={getFormattedKPIValue(item.data as KPI)}>
                                                {getFormattedKPIValue(item.data as KPI)}
                                            </span>
                                            {renderKPIDelta(item.data as KPI)}
                                        </div>
                                    </div>
                                </div>
                            )
                        )}

                        {/* Chart Rendering */}
                        {item.type === 'chart' && (
                            <div className={styles.chartCard}>
                                {isLoading ? (
                                    <div
                                        style={{
                                            height: '100%', display: 'flex', flexDirection: 'column', gap: 16
                                        }}
                                    >
                                        <Skeleton width="30%" height={24} />
                                        <Skeleton width="100%" height="100%" style={{ flex: 1, borderRadius: 8 }} />
                                    </div>
                                ) : (
                                    <>
                                        <div className={styles.chartHeader}>
                                            <div className={styles.chartTitleWrapper}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        minWidth: 0,
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            height: '20px',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        <DataSourceIcons
                                                            sources={(item.data as Chart).sources}
                                                        />
                                                    </div>

                                                    <MetricTooltip
                                                        title={item.data.title}
                                                        formula={(item.data as Chart).formula}
                                                        detail={(item.data as Chart).sql_detail}
                                                        sql={(item.data as Chart).sql}
                                                    >
                                                        <h3>{item.data.title}</h3>
                                                    </MetricTooltip>
                                                </div>
                                            </div>
                                            <div className={styles.cardControls}>
                                                {isEditMode ? (
                                                    <>
                                                        <button
                                                            className={styles.deleteButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(item.key);
                                                            }}
                                                        >
                                                            <Trash2 size={16} />
                                                            <span className={styles.deleteText}>Remove</span>
                                                        </button>
                                                        <Tooltip content="Drill down">
                                                            <button
                                                                className={styles.drillDownButton}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openBreakdown('chart', item.data);
                                                                }}
                                                            >
                                                                <Icon name="arrow_fat" size={16} />
                                                            </button>
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Tooltip content="Ask AI">
                                                            <div
                                                                className={styles.askAi}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAskAi(item.type, item.data);
                                                                }}
                                                            >
                                                                <Icon name="sparkles" size={16} />
                                                                <span className={styles.askAiText}>Ask AI</span>
                                                            </div>
                                                        </Tooltip>
                                                        <Tooltip content="Drill down">
                                                            <button
                                                                className={styles.drillDownButton}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openBreakdown('chart', item.data);
                                                                }}
                                                            >
                                                                <Icon name="arrow_fat" size={16} />
                                                            </button>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className={styles.chartContent}>
                                            <ChartRenderer
                                                chart={item.data as Chart}
                                                colSpan={layout.find((l: any) => l.i === item.key)?.w}
                                                chartsReady={chartsReady}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        <div className={styles.resizeOverlay}>
                            <div id={`resize-badge-${item.key}`} className={styles.resizeBadge}>
                            </div>
                            <div className={styles.resizeBorder} />
                        </div>
                    </div>
                </div>
            ))}
        </GridLayout>
    );
};
